// src/components/map/RouteIconsManager.js

/**
 * RouteIconsManager
 * 
 * A manager class for associating icons with specific routes on the map.
 * This allows filtering and toggling icons by route and keeps track of icon-route relationships.
 */
export class RouteIconsManager {
  constructor() {
    // Map to store route-icon associations (routeIndex -> icons[])
    this.routeIcons = new Map();
  }

  /**
   * Register icons for a specific route
   * 
   * @param {number} routeIndex - The route index to associate icons with
   * @param {Array} icons - Array of icon objects to register
   */
  registerIcons(routeIndex, icons) {
    this.routeIcons.set(routeIndex, icons);
  }

  /**
   * Get all icons associated with a specific route
   * 
   * @param {number} routeIndex - The route index to get icons for
   * @returns {Array} Array of icon objects associated with the route
   */
  getIconsForRoute(routeIndex) {
    return this.routeIcons.get(routeIndex) || [];
  }

  /**
   * Clear icons for a specific route
   * 
   * @param {number} routeIndex - The route index to clear icons for
   */
  clearRouteIcons(routeIndex) {
    this.routeIcons.delete(routeIndex);
  }

  /**
   * Get all registered routes
   * 
   * @returns {Array} Array of route indexes
   */
  getRegisteredRoutes() {
    return Array.from(this.routeIcons.keys());
  }

  /**
   * Get total icon count across all routes
   * 
   * @returns {number} Total number of icons registered
   */
  getTotalIconCount() {
    let count = 0;
    this.routeIcons.forEach(icons => {
      count += icons.length;
    });
    return count;
  }

  /**
   * Get all icons by type
   * 
   * @param {string} type - Icon type to filter by
   * @returns {Array} Array of icons matching the type
   */
  getIconsByType(type) {
    const result = [];
    this.routeIcons.forEach(icons => {
      icons.forEach(icon => {
        if (icon.type === type) {
          result.push(icon);
        }
      });
    });
    return result;
  }

  /**
   * Clear all route-icon associations
   */
  clearAll() {
    this.routeIcons.clear();
  }
}